import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  inject,
  DestroyRef,
  NgZone,
  ChangeDetectorRef,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Subject, finalize, forkJoin } from 'rxjs';
import { DrawerModule } from 'primeng/drawer';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';

import { MenuModule } from 'primeng/menu';
import { SelectModule } from 'primeng/select';

import { MessageService, MenuItem } from 'primeng/api';
import {
  ContratoEfetivoService,
  EfetivoService,
  FuncaoService,
  CategoriaService,
  TurnoService,
} from '@core/services/domain-services';
import {
  Efetivo,
  EstadoEfetivoEnum,
  Posto,
  Turno,
} from '@core/models/models';
import { ButtonModule } from 'primeng/button';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';

@Component({
  selector: 'app-posto-efetivos-alocados-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    DrawerModule,
    DatePickerModule,
    TooltipModule,
    SkeletonModule,
    MenuModule,
    SelectModule,
    PaginatorControlComponent,
    ButtonModule,
  ],
  templateUrl: './posto-efetivos-alocados-tab.component.html',
  styleUrl: './posto-efetivos-alocados-tab.component.scss',
})
export class PostoEfetivosAlocadosTabComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() postoId!: number;
  @Input() posto: Posto | null = null;
  @Output() countChange = new EventEmitter<number>();

  private readonly contratoEfetivoSvc = inject(ContratoEfetivoService);
  private readonly efetivoSvc = inject(EfetivoService);
  private readonly funcaoSvc = inject(FuncaoService);
  private readonly categoriaSvc = inject(CategoriaService);
  private readonly turnoSvc = inject(TurnoService);
  private readonly msgService = inject(MessageService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  readonly router = inject(Router);

  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<string>();
  private readonly cdr = inject(ChangeDetectorRef);
  contratos: any[] = [];
  loading = false;
  drawerContratosTotalRec = 0;
  page = 1;
  pageSize = 10;

  showFiltro = false;
  filtroTurno: number | null = null;
  turnos: Turno[] = [];
  menuContratosMap = new Map<number, MenuItem[]>();

  showAtribuirDrawer = false;
  atribuirForm!: FormGroup;
  atribuirMode: 'single' | 'multiple' = 'single';
  atribuirSelecionados: Efetivo[] = [];
  atribuirEfetivos: Efetivo[] = [];
  atribuirTotal = 0;
  atribuirPage = 1;
  atribuirPageSize = 10;
  atribuirSearch = '';
  atribuirFuncaoId: number | null = null;
  atribuirCategoriaId: number | null = null;
  atribuirFuncoes: Array<{ label: string; value: number }> = [];
  atribuirCategorias: Array<{ label: string; value: number }> = [];
  atribuirEfetivosLoading = false;
  atribuirSubmitting = false;
  atribuirDocumento: File | null = null;

  showDesatribuirDialog = false;
  desatribuirContrato: any = null;
  desatribuirSubmitting = false;
  desatribuirForm!: FormGroup;

  efetivosActivos: Array<{ label: string; value: number }> = [];
  loadingEfetivosActivos = false;

  readonly motivosRemocao = [
    { label: 'Transferência', value: 'TRANSFERENCIA', icon: 'ri-exchange-line' },
    { label: 'Fim de contrato', value: 'FIM_CONTRATO', icon: 'ri-file-close-line' },
    { label: 'Rescisão', value: 'RESCISAO', icon: 'ri-close-circle-line' },
    { label: 'Suspensão', value: 'SUSPENSAO', icon: 'ri-pause-circle-line' },
    { label: 'Reforma', value: 'REFORMA', icon: 'ri-walk-line' },
    { label: 'Reestruturação', value: 'REESTRUTURACAO', icon: 'ri-refresh-line' },
    { label: 'Outro', value: 'OUTRO', icon: 'ri-more-line' },
  ];

  ngOnInit(): void {
    this.atribuirForm = this.fb.group({
      dataInicio: [null, Validators.required],
      dataFim: [null],
    });

    this.desatribuirForm = this.fb.group({
      motivoRemocao: ['', Validators.required],
      motivoOutro: [''],
      dataRemocao: [new Date(), Validators.required],
      observacaoRemocao: [''],
      idEfetivoSubstituto: [null],
      confirmadoPorSupervisor: [false],
    });

    // Ao mudar o motivo de remoção, ajusta dinamicamente a validação de 'motivoOutro'
    this.desatribuirForm
      .get('motivoRemocao')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        const outroControl = this.desatribuirForm.get('motivoOutro');
        if (val === 'OUTRO') {
          outroControl?.setValidators([Validators.required]);
        } else {
          outroControl?.clearValidators();
          outroControl?.setValue('');
        }
        outroControl?.updateValueAndValidity();
        this.cdr.detectChanges();
      });

    this.searchSubject
      .pipe(debounceTime(350), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.atribuirPage = 1;
        this.loadAtribuirEfetivos();
      });

    this.load();
    this.loadTurnos();
    this.loadAtribuirFiltros();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['postoId'] && !changes['postoId'].firstChange) {
      this.page = 1;
      this.filtroTurno = null;
      this.contratos = [];
      this.showAtribuirDrawer = false;
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    const params: Record<string, unknown> = {
      IdPosto: this.postoId,
      pageNumber: 1,
      // Carrega os contratos do posto para filtrar antes da paginação local.
      pageSize: 1000,
    };

    this.contratoEfetivoSvc
      .getAll(params)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (res) => {
          const todosContratos = [...(res.data ?? [])];
          const contratosFiltrados = this.filtrarContratos(todosContratos);
          this.drawerContratosTotalRec = contratosFiltrados.length;
          const inicio = (this.page - 1) * this.pageSize;
          this.contratos = contratosFiltrados.slice(
            inicio,
            inicio + this.pageSize,
          );
          this.loading = false;
          this.buildMenuMap();
          this.atualizarContagemAtivos(contratosFiltrados);
        },
        error: () => {
          this.contratos = [];
          this.drawerContratosTotalRec = 0;
          this.countChange.emit(0);
        },
      });
  }

  private filtrarContratos(contratos: any[]): any[] {
    return contratos.filter((contrato) => {
      if (this.filtroTurno != null) {
        const turnoId =
          contrato.turnoId ??
          contrato.idTurno ??
          contrato.turno?.id ??
          contrato.turno?.turnoId ??
          contrato.escala?.turnoId ??
          contrato.escala?.idTurno ??
          contrato.escala?.turno?.id ??
          contrato.escala?.turno?.turnoId;
        if (Number(turnoId) !== this.filtroTurno) return false;
      }

      return true;
    });
  }

  private atualizarContagemAtivos(contratos: any[] = this.contratos): void {
    const ativosCount = contratos.filter(
      (c) => this.getEstadoContrato(c).label !== 'Encerrado'
    ).length;
    this.countChange.emit(ativosCount);
  }

  /** Constrói o menu de ações de cada linha. */
  buildMenuMap(): void {
    this.menuContratosMap.clear();
    for (const c of this.contratos) {
      const podeRemover = !this.isEscalaConcluida(c);

      const menuItems: MenuItem[] = [
        {
          label: 'Ver efetivo',
          icon: 'ri-external-link-line',
          command: () => this.router.navigate(['/admin/efetivos', c.idEfetivo]),
        },
      ];

      if (podeRemover) {
        menuItems.push(
          { separator: true },
          {
            label: 'Remover do posto',
            icon: 'ri-user-unfollow-line',
            styleClass: 'menu-item-danger',
            command: () => this.openDesatribuirDialog(c),
          },
        );
      }

      this.menuContratosMap.set(c.id, menuItems);
    }
  }

  loadTurnos(): void {
    this.turnoSvc
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.turnos = res?.data ?? (Array.isArray(res) ? res : []);
        },
        error: () => {
          this.turnos = [];
        },
      });
  }

  loadEfetivosActivosSubstitutos(): void {
    this.loadingEfetivosActivos = true;
    this.efetivoSvc
      .getAll({ pageSize: 1000, Ativo: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loadingEfetivosActivos = false))
      )
      .subscribe({
        next: (res: any) => {
          const list = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res)
              ? res
              : [];
          const idAtual = this.desatribuirContrato?.idEfetivo;
          this.efetivosActivos = list
            .filter((e: any) => e.id !== idAtual)
            .map((e: any) => ({
              label: `${e.nome} (${e.numTelefone || 'G-' + ('000' + e.id).slice(-4)})`,
              value: Number(e.id),
            }));
        },
        error: () => {
          this.efetivosActivos = [];
        },
      });
  }

  onPageChange(ev: { page?: number; rows?: number }): void {
    const nextPageSize = ev.rows ?? this.pageSize;
    this.page = nextPageSize !== this.pageSize ? 1 : (ev.page ?? 0) + 1;
    this.pageSize = nextPageSize;
    this.load();
  }

  applyFiltros(): void {
    this.page = 1;
    this.load();
  }

  clearFiltros(): void {
    this.filtroTurno = null;
    this.page = 1;
    this.load();
  }

  openAtribuirDrawer(): void {
    this.atribuirSelecionados = [];
    this.atribuirSearch = '';
    this.atribuirPage = 1;
    this.atribuirPageSize = 10;
    this.atribuirFuncaoId = null;
    this.atribuirCategoriaId = null;
    this.atribuirDocumento = null;
    this.atribuirForm.reset({ dataInicio: new Date() });
    this.showAtribuirDrawer = true;
    this.loadAtribuirEfetivos();
  }

  fecharAtribuirDrawer(): void {
    this.showAtribuirDrawer = false;
    this.atribuirSubmitting = false;
    this.atribuirSelecionados = [];
  }

  loadAtribuirEfetivos(): void {
    this.atribuirEfetivosLoading = true;

    const efetivosParams: Record<string, unknown> = {
      pageNumber: this.atribuirPage,
      pageSize: this.atribuirPageSize,
      IsSemContrato: true,
      EfetivosSemEscala: true,
      EstadoEfetivo: EstadoEfetivoEnum.ATIVO,
    };

    if (this.atribuirSearch?.trim()) {
      efetivosParams['Nome'] = this.atribuirSearch.trim();
    }
    if (this.atribuirFuncaoId != null) {
      efetivosParams['IdFuncao'] = this.atribuirFuncaoId;
    }
    if (this.atribuirCategoriaId != null) {
      efetivosParams['IdCategoria'] = this.atribuirCategoriaId;
    }

    this.carregarEfetivosDisponiveis(efetivosParams);
  }

  private carregarEfetivosDisponiveis(params: Record<string, unknown>): void {
    this.efetivoSvc
      .getAll(params)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.atribuirEfetivosLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res: any) => {
          const data = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res)
              ? res
              : [];
          this.atribuirEfetivos = [...data];
          this.atribuirTotal = res?.totalRecords ?? data.length;
          this.atribuirEfetivosLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.atribuirEfetivos = [];
          this.atribuirTotal = 0;
          this.atribuirEfetivosLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  onAtribuirSearch(): void {
    this.searchSubject.next(this.atribuirSearch);
  }

  onAtribuirPageChange(ev: { page?: number; rows?: number }): void {
    const nextPageSize = ev.rows ?? this.atribuirPageSize;
    this.atribuirPage =
      nextPageSize !== this.atribuirPageSize ? 1 : (ev.page ?? 0) + 1;
    this.atribuirPageSize = nextPageSize;
    this.loadAtribuirEfetivos();
  }

  onAtribuirFilterChange(): void {
    this.atribuirPage = 1;
    this.loadAtribuirEfetivos();
  }

  clearAtribuirFilters(): void {
    this.atribuirSearch = '';
    this.atribuirFuncaoId = null;
    this.atribuirCategoriaId = null;
    this.atribuirPage = 1;
    this.loadAtribuirEfetivos();
  }

  get hasAtribuirFilters(): boolean {
    return Boolean(
      this.atribuirSearch.trim() ||
        this.atribuirFuncaoId != null ||
        this.atribuirCategoriaId != null,
    );
  }

  private loadAtribuirFiltros(): void {
    forkJoin({
      funcoes: this.funcaoSvc.getAll({ pageSize: 1000 }),
      categorias: this.categoriaSvc.getAll({ pageSize: 1000 }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ funcoes, categorias }) => {
          this.atribuirFuncoes = (funcoes?.data ?? [])
            .filter((item) => item.id != null)
            .map((item) => ({
              label: item.designacao,
              value: Number(item.id),
            }));
          this.atribuirCategorias = (categorias?.data ?? [])
            .filter((item) => item.id != null)
            .map((item) => ({
              label: item.designacao,
              value: Number(item.id),
            }));
        },
        error: () => {
          this.atribuirFuncoes = [];
          this.atribuirCategorias = [];
        },
      });
  }

  setAtribuirMode(mode: 'single' | 'multiple'): void {
    this.atribuirMode = mode;
    if (mode === 'single' && this.atribuirSelecionados.length > 1)
      this.atribuirSelecionados = [this.atribuirSelecionados[0]];
  }

  isSelected(ef: Efetivo): boolean {
    return this.atribuirSelecionados.some((s) => s.id === ef.id);
  }

  toggleSelecionado(ef: Efetivo): void {
    if (this.isSelected(ef)) {
      this.atribuirSelecionados = this.atribuirSelecionados.filter(
        (s) => s.id !== ef.id,
      );
    } else {
      this.atribuirSelecionados =
        this.atribuirMode === 'single'
          ? [ef]
          : [...this.atribuirSelecionados, ef];
    }
  }

  removerSelecionado(ef: Efetivo): void {
    this.atribuirSelecionados = this.atribuirSelecionados.filter(
      (s) => s.id !== ef.id,
    );
  }

  clearAtribuirSelection(): void {
    this.atribuirSelecionados = [];
  }

  toggleCurrentPageSelection(): void {
    const available = this.atribuirEfetivos.filter(
      (ef) => !this.isJaAlocado(ef),
    );
    const availableIds = new Set(available.map((ef) => ef.id));

    if (this.isCurrentPageFullySelected) {
      this.atribuirSelecionados = this.atribuirSelecionados.filter(
        (ef) => !availableIds.has(ef.id),
      );
      return;
    }

    const selectedIds = new Set(this.atribuirSelecionados.map((ef) => ef.id));
    this.atribuirSelecionados = [
      ...this.atribuirSelecionados,
      ...available.filter((ef) => !selectedIds.has(ef.id)),
    ];
  }

  get currentPageSelectedCount(): number {
    const currentPageIds = new Set(this.atribuirEfetivos.map((ef) => ef.id));
    return this.atribuirSelecionados.filter((ef) => currentPageIds.has(ef.id))
      .length;
  }

  get isCurrentPageFullySelected(): boolean {
    const available = this.atribuirEfetivos.filter(
      (ef) => !this.isJaAlocado(ef),
    );
    return available.length > 0 && available.every((ef) => this.isSelected(ef));
  }

  isJaAlocado(ef: Efetivo): boolean {
    return this.contratos.some(
      (c) => c.idEfetivo === ef.id && this.getEstadoContrato(c).label !== 'Encerrado'
    );
  }

  confirmarAtribuicao(): void {
    if (this.atribuirForm.invalid || !this.atribuirSelecionados.length) {
      this.atribuirForm.markAllAsTouched();
      return;
    }
    this.atribuirSubmitting = true;
    const v = this.atribuirForm.value;

    if (this.atribuirSelecionados.length === 1) {
      const fd = new FormData();
      fd.append('IdEfetivo', String(this.atribuirSelecionados[0].id));
      fd.append('IdPosto', String(this.postoId));
      fd.append('DataInicio', this.toISO(v.dataInicio));
      if (v.dataFim) fd.append('DataFim', this.toISO(v.dataFim));
      if (this.atribuirDocumento)
        fd.append('Documento', this.atribuirDocumento);
      this.contratoEfetivoSvc
        .createWithFormData(fd)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.onAtribuicaoSucesso(),
          error: () => this.onAtribuicaoErro(),
        });
    } else {
      const fd = new FormData();
      this.atribuirSelecionados.forEach((ef, i) => {
        fd.append(`requests[${i}].idEfetivo`, String(ef.id));
        fd.append(`requests[${i}].dataInicio`, this.toISO(v.dataInicio));
        if (v.dataFim)
          fd.append(`requests[${i}].dataFim`, this.toISO(v.dataFim));
      });
      this.contratoEfetivoSvc
        .createRangeFormData(this.postoId, fd)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.onAtribuicaoSucesso(),
          error: () => this.onAtribuicaoErro(),
        });
    }
  }

  private onAtribuicaoSucesso(): void {
    this.msgService.add({
      severity: 'success',
      summary: 'Alocação registada',
      detail: 'Efetivo(s) alocado(s) com sucesso.',
      life: 3000,
    });
    this.fecharAtribuirDrawer();
    this.load();
  }

  private onAtribuicaoErro(): void {
    this.msgService.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Não foi possível alocar o efetivo ao posto.',
      life: 3000,
    });
    this.atribuirSubmitting = false;
  }

  openDesatribuirDialog(c: any): void {
    if (this.isEscalaConcluida(c)) return;

    this.desatribuirContrato = c;
    this.desatribuirForm.reset({
      motivoRemocao: '',
      motivoOutro: '',
      dataRemocao: new Date(),
      observacaoRemocao: '',
      idEfetivoSubstituto: null,
      confirmadoPorSupervisor: false,
    });
    this.loadEfetivosActivosSubstitutos();
    this.showDesatribuirDialog = true;
  }

  fecharDesatribuirDialog(): void {
    this.showDesatribuirDialog = false;
    this.desatribuirSubmitting = false;
    this.desatribuirContrato = null;
  }

  confirmarDesatribuicao(): void {
    if (this.desatribuirForm.invalid) {
      this.desatribuirForm.markAllAsTouched();
      return;
    }
    const id = this.desatribuirContrato?.id;
    if (!id) return;

    this.desatribuirSubmitting = true;
    const v = this.desatribuirForm.value;

    const payload = {
      idContratoEfetivo: id,
      motivoRemocao: v.motivoRemocao ?? '',
      motivoOutro: v.motivoRemocao === 'OUTRO' ? (v.motivoOutro ?? '') : '',
      dataRemocao: this.toISO(v.dataRemocao ?? new Date()),
      observacaoRemocao: v.observacaoRemocao ?? '',
      isRemocao: true,
      idEfetivoSubstituto: v.idEfetivoSubstituto ? Number(v.idEfetivoSubstituto) : 0,
      confirmadoPorSupervisor: !!v.confirmadoPorSupervisor,
    };

    this.contratoEfetivoSvc
      .remover(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.msgService.add({
            severity: 'success',
            summary: 'Efetivo removido',
            detail: 'Alocação encerrada com sucesso.',
            life: 3000,
          });
          this.fecharDesatribuirDialog();
          this.load();
        },
        error: () => {
          this.msgService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao remover efetivo do posto.',
            life: 3000,
          });
          this.desatribuirSubmitting = false;
        },
      });
  }

  onFileChange(event: Event): void {
    const f = (event.target as HTMLInputElement).files;
    this.atribuirDocumento = f?.length ? f[0] : null;
  }

  onImgError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  getInitials(nome: string): string {
    if (!nome) return '?';
    const parts = nome.trim().split(' ').filter(Boolean);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }

  getEstadoContrato(c: any): { label: string; css: string } {
    const agora = new Date();
    const ini = c.dataInicio ? new Date(c.dataInicio) : null;
    const fim = c.dataFim ? new Date(c.dataFim) : null;
    if (c.isRemocao || c.motivoRemocao)
      return { label: 'Encerrado', css: 'badge-estado--red' };
    if (fim && !Number.isNaN(fim.getTime()) && fim < agora)
      return { label: 'Concluída', css: 'badge-estado--grey' };

    const estadoApi = String(
      c.estadoContratoDescricao ?? c.estadoContrato?.descricao ?? c.estadoContrato ?? '',
    )
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
    if (estadoApi.includes('conclu'))
      return { label: 'Concluída', css: 'badge-estado--grey' };

    // A API pode marcar o contrato como decorrendo antes da hora de início.
    // Nesse caso, a data/hora agendada prevalece até o início efetivo.
    if (ini && !Number.isNaN(ini.getTime()) && ini > agora)
      return { label: 'Futuro', css: 'badge-estado--blue' };
    return { label: 'Vigente', css: 'badge-estado--green' };
  }

  isEscalaConcluida(c: any): boolean {
    if (!c) return false;
    const estado = this.getEstadoContrato(c);
    return estado.label === 'Vigente' || estado.label === 'Concluída' || estado.label === 'Encerrado';
  }

  formatDate(d: string | Date | null | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('pt-AO');
  }

  getTurnoIconFromTurno(t: Turno): string {
    return this.resolveTurnoIcon(t.icon);
  }

  getTurnoColorFromTurno(t: Turno): string {
    return this.resolveTurnoColor(t.icon);
  }

  getEfetivoEstado(ef: Efetivo): string {
    return (
      ef.estadoEfetivoDescricao?.trim() ||
      (ef.ativo === false ? 'Inativo' : 'Ativo')
    );
  }

  getEfetivoEstadoClass(ef: Efetivo): string {
    const estado = this.getEfetivoEstado(ef).toLowerCase();
    if (estado.includes('fer')) return 'status-leave';
    if (estado.includes('inativ') || estado.includes('suspens')) {
      return 'status-inactive';
    }
    if (estado.includes('afast')) return 'status-away';
    return 'status-active';
  }

  getEfetivoEstadoIcon(ef: Efetivo): string {
    const css = this.getEfetivoEstadoClass(ef);
    if (css === 'status-leave') return 'ri-suitcase-line';
    if (css === 'status-inactive') return 'ri-pause-circle-line';
    if (css === 'status-away') return 'ri-user-unfollow-line';
    return 'ri-checkbox-circle-line';
  }

  trackById(_: number, item: any): number {
    return item.id;
  }

  private resolveTurnoIcon(icon?: string | null): string {
    return icon?.trim() || 'pi pi-clock';
  }

  private resolveTurnoColor(icon?: string | null): string {
    const normalized = icon?.toLowerCase() ?? '';
    if (normalized.includes('sun')) return '#f59e0b';
    if (normalized.includes('moon')) return '#a78bfa';
    return '#38bdf8';
  }

  private toISO(d: any): string {
    if (!d) return '';
    if (typeof d === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
      d = new Date(d);
    }
    if (d instanceof Date && !isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  }

  get atribuirFormInvalid(): boolean {
    return (
      this.atribuirForm.invalid ||
      !this.atribuirSelecionados.length ||
      this.atribuirSubmitting
    );
  }

  get desatribuirFormInvalid(): boolean {
    if (!this.desatribuirForm) return true;
    return this.desatribuirForm.invalid || this.desatribuirSubmitting;
  }
}
